// v1.22.0 · Auditoría · Expediente mensual: subir documentos (sueltos o el .zip del cierre), cobertura por mes y categoría,
// lista de archivos con su tipo reconocido, cambiar categoría/período/cuenta, descargar y borrar.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Download, FileWarning, Pencil, Trash2, UploadCloud } from 'lucide-react';
import { C, KIND_LABEL, api, download, errMsg, fdate, fsize, plab, uploadFile, waitJob } from './api';

export default function Expediente({ cid, analysis, onChanged }) {
  const [over, setOver] = useState(false);
  const [queue, setQueue] = useState([]);      // [{name, pct, status, job}]
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState({ category: '', period: '', accountCode: '' });
  const [files, setFiles] = useState({ total: 0, items: [], categories: [] });
  const [flt, setFlt] = useState({ category: '', period: '', q: '' });
  const [edit, setEdit] = useState(null);
  const input = useRef(null);

  const load = useCallback(async () => {
    try { setFiles(await api.get(`/companies/${cid}/files`, { ...flt, limit: 300 })); } catch (e) { toast.error(errMsg(e)); }
  }, [cid, flt]);
  useEffect(() => { load(); }, [load]);

  const send = async (list) => {
    if (!list.length || busy) return;
    setBusy(true);
    const items = Array.from(list).map((f) => ({ name: f.name, size: f.size, pct: 0, status: 'Subiendo', file: f }));
    setQueue(items);
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      const upd = (p) => setQueue((q) => q.map((x, j) => (j === i ? { ...x, ...p } : x)));
      try {
        const job = await uploadFile(cid, it.file, { ...opts, onProgress: (p) => upd({ pct: Math.round(p * 100) }) });
        upd({ status: 'Analizando', pct: 100 });
        const done = await waitJob(job.id, (j) => upd({ status: j.message, prog: j.progress }));
        upd({ status: done.status === 'done' ? 'Listo' : 'Error', result: done.result, log: done.log, error: done.status === 'error' ? done.message : '' });
      } catch (e) {
        upd({ status: 'Error', error: errMsg(e) });
      }
    }
    setBusy(false);
    load();
    onChanged();
  };

  const cats = files.categories || [];
  const cov = analysis?.coverage || { periods: [], cells: {}, categories: [] };

  return (
    <div className="neu-grid" style={{ gap: 22 }}>
      <section className="neu-card">
        <p className="neu-title">Subir documentos del mes</p>
        <p className="neu-sub" style={{ marginBottom: 12 }}>
          Plan de cuentas, balance, estado de resultados, mayores, estados de cuenta bancarios y de tarjeta, formularios 103/104,
          planillas IESS, carteras, anexos, roles o el .zip completo del cierre. El tipo se reconoce solo; cada mes se acumula con lo anterior
          y un archivo repetido no se vuelve a cargar.
        </p>
        <div className={`neu-drop ${over ? 'is-over' : ''}`} role="button" tabIndex={0} aria-label="Elegir archivos para subir"
             onClick={() => input.current?.click()} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
             onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
             onDrop={(e) => { e.preventDefault(); setOver(false); send(e.dataTransfer.files); }}>
          <UploadCloud size={34} color={C.brand} />
          <p style={{ fontWeight: 700, margin: '8px 0 2px' }}>{busy ? 'Procesando…' : 'Arrastre aquí los archivos o el .zip, o haga clic para elegirlos'}</p>
          <p className="neu-hint">Excel (.xlsx, .xls), PDF, Word, CSV o .zip · hasta 600 MB</p>
          <input ref={input} type="file" multiple hidden onChange={(e) => { send(e.target.files); e.target.value = ''; }}
                 accept=".xlsx,.xls,.xlsm,.csv,.pdf,.docx,.zip,.png,.jpg,.jpeg,.htm,.html" />
        </div>
        <details style={{ marginTop: 12 }}>
          <summary className="neu-hint" style={{ cursor: 'pointer' }}>Opciones (solo si el sistema no reconoce bien el archivo)</summary>
          <div className="neu-grid neu-grid-3" style={{ marginTop: 10 }}>
            <label className="neu-field"><span className="neu-label">Categoría</span>
              <select className="neu-select" value={opts.category} onChange={(e) => setOpts({ ...opts, category: e.target.value })}>
                <option value="">Automática</option>{cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></label>
            <label className="neu-field"><span className="neu-label">Mes</span>
              <input className="neu-input" type="month" value={opts.period} onChange={(e) => setOpts({ ...opts, period: e.target.value })} /></label>
            <label className="neu-field"><span className="neu-label">Cuenta contable (estado bancario)</span>
              <input className="neu-input" placeholder="Ej. 1.1.1.3.6" value={opts.accountCode} onChange={(e) => setOpts({ ...opts, accountCode: e.target.value })} /></label>
          </div>
        </details>
        {queue.length > 0 && (
          <div className="neu-grid" style={{ gap: 10, marginTop: 16 }}>
            {queue.map((q, i) => (
              <div key={i} className="neu-card-flat">
                <div className="neu-row"><b style={{ fontSize: 13, wordBreak: 'break-all' }}>{q.name}</b><span className="neu-sub">{fsize(q.size)}</span><span className="neu-spacer" />
                  <span className="neu-sub" style={{ color: q.status === 'Error' ? '#b42318' : q.status === 'Listo' ? '#067647' : C.ink2 }}>{q.status}</span></div>
                {q.status !== 'Listo' && q.status !== 'Error' && <div className="neu-progress" style={{ marginTop: 8 }}><i style={{ width: `${q.status === 'Subiendo' ? q.pct : (q.prog || 5)}%` }} /></div>}
                {q.result && (
                  <p className="neu-hint" style={{ marginTop: 6 }}>
                    <CheckCircle2 size={13} color="#067647" style={{ verticalAlign: -2 }} /> {q.result.procesados} archivo(s) · {q.result.reconocidos} reconocido(s)
                    {q.result.repetidos ? ` · ${q.result.repetidos} ya estaban cargados` : ''}{q.result.errores ? ` · ${q.result.errores} con aviso` : ''}
                    {Object.keys(q.result.por_tipo || {}).length > 0 && ` — ${Object.entries(q.result.por_tipo).map(([k, v]) => `${KIND_LABEL[k] || k}: ${v}`).join(', ')}`}
                  </p>
                )}
                {q.error && <p className="neu-hint" style={{ color: '#b42318' }}>{q.error}</p>}
                {q.log?.length > 0 && <details><summary className="neu-hint">Avisos ({q.log.length})</summary>
                  <ul className="neu-hint">{q.log.slice(0, 30).map((l, j) => <li key={j}><b>{l.file}</b>: {l.msg}</li>)}</ul></details>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="neu-card">
        <p className="neu-title">Cobertura del expediente</p>
        <p className="neu-sub" style={{ marginBottom: 12 }}>Documentos por mes y categoría. Una celda vacía en un mes revisado es un soporte que falta pedir.</p>
        <div className="neu-scroll" style={{ maxHeight: 420 }}>
          <table className="neu-table">
            <thead><tr><th>Categoría</th>{cov.periods.map((p) => <th key={p} className="num">{plab(p)}</th>)}</tr></thead>
            <tbody>
              {cov.categories.map((c) => (
                <tr key={c.key}>
                  <td style={{ whiteSpace: 'nowrap' }}><button type="button" className="neu-btn neu-btn-sm" onClick={() => setFlt({ ...flt, category: c.key, period: '' })}>{c.label}</button></td>
                  {cov.periods.map((p) => {
                    const n = cov.cells?.[p]?.[c.key] || 0;
                    return <td key={p} className="num"><button type="button" onClick={() => n && setFlt({ category: c.key, period: p === 'sin período' ? 'none' : p, q: '' })}
                      className="neu-cell" style={{ border: 0, width: '100%', cursor: n ? 'pointer' : 'default', background: n ? `rgba(42,120,214,${Math.min(0.85, 0.18 + n * 0.06)})` : 'transparent',
                        color: n > 6 ? '#fff' : n ? C.ink : '#a3abb8' }} aria-label={`${c.label} ${plab(p)}: ${n}`}>{n || '·'}</button></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="neu-card">
        <div className="neu-row" style={{ marginBottom: 10 }}>
          <p className="neu-title">Archivos ({files.total})</p><span className="neu-spacer" />
          <select className="neu-select" style={{ width: 'auto' }} value={flt.category} onChange={(e) => setFlt({ ...flt, category: e.target.value })} aria-label="Categoría">
            <option value="">Todas las categorías</option>{cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select>
          <select className="neu-select" style={{ width: 'auto' }} value={flt.period} onChange={(e) => setFlt({ ...flt, period: e.target.value })} aria-label="Mes">
            <option value="">Todos los meses</option>{cov.periods.filter((p) => p !== 'sin período').map((p) => <option key={p} value={p}>{plab(p)}</option>)}<option value="none">Sin período</option></select>
          <input className="neu-input" style={{ width: 180 }} placeholder="Buscar nombre" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} />
        </div>
        <div className="neu-scroll" style={{ maxHeight: 560 }}>
          <table className="neu-table">
            <thead><tr><th>Archivo</th><th>Tipo reconocido</th><th>Categoría</th><th>Mes</th><th className="num">Tamaño</th><th /></tr></thead>
            <tbody>
              {files.items.map((f) => (
                <tr key={f.id}>
                  <td style={{ maxWidth: 320, wordBreak: 'break-word' }}><b style={{ fontSize: 12.5 }}>{f.name}</b>
                    <div className="neu-hint" style={{ fontSize: 11 }}>{f.path !== f.name ? f.path : ''} · {fdate(f.created_at)}</div>
                    {f.error && <div className="neu-hint" style={{ color: '#9a6700' }}><FileWarning size={12} style={{ verticalAlign: -2 }} /> {f.error}</div>}</td>
                  <td><span className={`neu-chip ${f.kind && f.kind !== 'documento' ? 'neu-chip-green' : 'neu-chip-gray'}`}>{KIND_LABEL[f.kind] || f.kind}</span>
                    {f.summary && Object.keys(f.summary).length > 0 && <div className="neu-hint" style={{ fontSize: 11, marginTop: 4 }}>{summaryText(f)}</div>}</td>
                  <td>{(cats.find((c) => c.key === f.category) || {}).label || f.category}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{plab(f.period)}</td>
                  <td className="num">{fsize(f.size)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button type="button" className="neu-btn neu-icon-btn" title="Cambiar categoría, mes o cuenta" aria-label="Editar" onClick={() => setEdit({ ...f, account_code: '' })}><Pencil size={14} /></button>{' '}
                    <button type="button" className="neu-btn neu-icon-btn" title="Descargar" aria-label="Descargar" onClick={() => download(`/files/${f.id}/download`, f.name).catch((e) => toast.error(errMsg(e)))}><Download size={14} /></button>{' '}
                    <button type="button" className="neu-btn neu-icon-btn neu-btn-danger" title="Borrar" aria-label="Borrar"
                            onClick={async () => { if (!window.confirm(`¿Borrar «${f.name}» y lo que se extrajo de él?`)) return; try { await api.del(`/files/${f.id}`); toast.success('Archivo borrado'); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); } }}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {!files.items.length && <tr><td colSpan={6} className="neu-empty">Sin archivos con este filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {edit && (
        <div className="neu-modal-bg" role="dialog" aria-modal="true" aria-label="Editar archivo" onClick={(e) => e.target === e.currentTarget && setEdit(null)}>
          <div className="neu-modal">
            <p className="neu-title" style={{ fontSize: 16 }}>{edit.name}</p>
            <div className="neu-grid" style={{ gap: 12, marginTop: 12 }}>
              <label className="neu-field"><span className="neu-label">Categoría</span>
                <select className="neu-select" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })}>{cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></label>
              <label className="neu-field"><span className="neu-label">Mes</span>
                <input className="neu-input" type="month" value={edit.period || ''} onChange={(e) => setEdit({ ...edit, period: e.target.value })} /></label>
              {['banco_resumen', 'banco_meses', 'tarjeta', 'cxc_cartera'].includes(edit.kind) && (
                <label className="neu-field"><span className="neu-label">Cuenta contable a la que corresponde (opcional)</span>
                  <input className="neu-input" placeholder="Ej. 1.1.1.3.6" value={edit.account_code} onChange={(e) => setEdit({ ...edit, account_code: e.target.value })} />
                  <span className="neu-hint">Úselo cuando el número de cuenta del banco no aparece en el nombre de la cuenta contable.</span></label>
              )}
            </div>
            <div className="neu-row" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
              <button type="button" className="neu-btn" onClick={() => setEdit(null)}>Cancelar</button>
              <button type="button" className="neu-btn neu-btn-primary" onClick={async () => {
                try {
                  await api.patch(`/files/${edit.id}`, { category: edit.category, period: edit.period || '', ...(edit.account_code ? { account_code: edit.account_code } : {}) });
                  toast.success('Guardado; análisis recalculado'); setEdit(null); load(); onChanged();
                } catch (e) { toast.error(errMsg(e)); }
              }}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function summaryText(f) {
  const s = f.summary || {};
  const sg = s.signo === 'negativo' ? ' · acreedoras venían en negativo (convertidas)' : '';
  if (f.kind === 'mayor') return `${s.movimientos} movimientos · ${s.desde} a ${s.hasta}${sg}`;
  if (f.kind === 'plan') return `${s.cuentas} cuentas`;
  if (f.kind === 'balance' || f.kind === 'pyg') return `Corte ${s.corte} · ${s.lineas} líneas${sg}`;
  if (f.kind === 'banco_resumen') return `Cta. ${s.account_number || '—'} · saldo ${Number(s.saldo_final || 0).toLocaleString('es-EC')}`;
  if (f.kind === 'tarjeta') return `Total a pagar ${Number(s.total_pagar || 0).toLocaleString('es-EC')} · exterior ${Number(s.exterior_total || 0).toLocaleString('es-EC')}`;
  if (f.kind === 'sri_103' || f.kind === 'sri_104') return `${s.period} · ${s.tipo}`;
  if (f.kind === 'cxp_cartera') return `Total ${Number(s.total || 0).toLocaleString('es-EC')} · ${s.documentos} documentos`;
  if (f.kind === 'cxc_cartera') return `${s.account_code} · contabilidad ${Number(s.total_contabilidad_cuenta || 0).toLocaleString('es-EC')}`;
  return '';
}
